import { BOARD, GROUPS } from '../game/board'
import { CRITTERS } from '../scene/critters'

/**
 * รายชื่อไฟล์อาร์ตทั้งหมดที่เกมรู้จัก — แหล่งความจริงเดียว
 *
 * ทั้งสคริปต์สร้างภาพหลอก ตัวโหลด และเอกสารสั่งงาน อ้างจากไฟล์นี้ที่เดียว
 * จะได้ไม่มีวันหลุดกันเอง ถ้าเพิ่มช่องใหม่ในกระดาน รายการนี้จะโตตามอัตโนมัติ
 *
 * ทุกชิ้น "ไม่บังคับ" — ถ้าไฟล์ไหนยังไม่มี เกมจะวาดแบบเดิมแทน (ดู loader.ts)
 * ทำแบบนี้เพื่อให้ทยอยใส่อาร์ตทีละกลุ่มได้ ไม่ต้องรอครบร้อยชิ้นถึงจะเล่นได้
 */

export const ART_BASE = '/art'

export type ArtKind = 'ground' | 'center' | 'char' | 'icon' | 'prop' | 'dice' | 'plate'

export interface ArtItem {
  /** คีย์ที่โค้ดใช้เรียก เช่น char:millo:happy */
  key: string
  kind: ArtKind
  /** พาธจริงใต้ public/ */
  path: string
  width: number
  height: number
  /** กลุ่มโหลด — first โหลดก่อนเข้าเกม ส่วน later ค่อยโหลดตามหลัง */
  tier: 'first' | 'later'
}

/** ท่าทางตัวละคร เรียงตามลำดับที่ควรมี */
export const POSES = ['idle', 'ready', 'happy', 'sad', 'cheer'] as const
export type Pose = (typeof POSES)[number]

/** ของประดับที่กระจายรอบกระดาน */
export const PROPS = [
  'rock-a',
  'rock-b',
  'rock-c',
  'bush-a',
  'bush-b',
  'tree-a',
  'tree-b',
  'crystal-a',
  'crystal-b',
  'antenna',
  'pipe',
  'solar',
] as const

function item(key: string, kind: ArtKind, path: string, width: number, height: number, tier: ArtItem['tier']): ArtItem {
  return { key, kind, path: `${ART_BASE}/${path}`, width, height, tier }
}

export const ART: ArtItem[] = [
  item('ground', 'ground', 'ground/ground.png', 4096, 4096, 'first'),
  // ลานกลางกับแถบสีกลุ่มเป็นหน้าตาแรกที่เห็นตอนเปิดกระดาน ต้องมาพร้อมกลุ่มแรก
  // ไม่งั้นผู้เล่นจะเห็นของวาดจากโค้ดแวบหนึ่งแล้วค่อยเปลี่ยนเป็นภาพวาด ดูเหมือนจอกระตุก
  item('center', 'center', 'center-emblem.png', 2048, 2048, 'first'),

  // ตัวละคร — ท่า idle ต้องมาก่อนเพราะใช้ตลอดเวลา ท่าอื่นค่อยตามมา
  ...Object.keys(CRITTERS).flatMap((id) =>
    POSES.map((pose) =>
      item(`char:${id}:${pose}`, 'char', `char/char-${id}-${pose}.png`, 512, 768, pose === 'idle' ? 'first' : 'later'),
    ),
  ),

  // ไอคอนช่อง — ใช้ชุดไอคอนที่กระดานอ้างถึงจริงเท่านั้น ไม่ได้ฮาร์ดโค้ด
  ...[...new Set(BOARD.map((t) => t.icon))].map((icon) =>
    item(`icon:${icon}`, 'icon', `icon/icon-${icon}.png`, 512, 512, 'first'),
  ),

  ...PROPS.map((p) => item(`prop:${p}`, 'prop', `prop/prop-${p}.png`, 512, 512, 'later')),
  ...[1, 2, 3, 4, 5, 6].map((n) => item(`dice:${n}`, 'dice', `dice/dice-${n}.png`, 512, 512, 'later')),
  ...Object.keys(GROUPS).map((g) => item(`plate:${g}`, 'plate', `plate/plate-${g}.png`, 1024, 256, 'first')),
]

export const ART_BY_KEY = new Map(ART.map((a) => [a.key, a]))

/** สรุปจำนวนไว้ใช้ในเอกสารและหน้าโหลด */
export function artSummary(): Record<ArtKind | 'total', number> {
  const out = { ground: 0, center: 0, char: 0, icon: 0, prop: 0, dice: 0, plate: 0, total: ART.length }
  ART.forEach((a) => (out[a.kind] += 1))
  return out
}
